import React, { useState, useRef } from "react";
import { Building2, ChevronDown } from "lucide-react";
import AnchoredPanel from "./AnchoredPanel";

// LGX-787: per-row office picker for the bulk "Add Team Member" form. Unlike OfficeDropdown.js
// (which this is adapted from), there is no existing user yet to call add-user/remove-user
// against -- selection is purely local state, reported to the parent row via onChange, and sent
// as part of that row's own invite payload on Send Invite.
const OfficeMultiSelect = ({ availableOffices = [], selectedCodes = [], onChange }) => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);

  const toggle = (officeCode) => {
    const next = selectedCodes.includes(officeCode)
      ? selectedCodes.filter((c) => c !== officeCode)
      : [...selectedCodes, officeCode];
    onChange(next);
  };

  const selectedNames = availableOffices
    .filter((o) => selectedCodes.includes(o.officeCode))
    .map((o) => o.name || o.officeCode);

  return (
    <div className="relative inline-block w-full" ref={anchorRef}>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="flex items-center justify-between gap-1 w-full min-w-[10rem] border border-gray-300 dark:border-acs-dark-border rounded px-2 py-1 bg-white dark:bg-acs-dark-surface text-left text-xs text-gray-900 dark:text-acs-dark-text"
      >
        <span className="truncate" title={selectedNames.join(", ")}>
          {selectedNames.length > 0
            ? `${selectedNames.length} office${selectedNames.length > 1 ? "s" : ""} selected`
            : "Select offices"}
        </span>
        <ChevronDown className="w-3.5 h-3.5 shrink-0 text-gray-400" />
      </button>

      {open && (
        <AnchoredPanel
          anchorRef={anchorRef}
          width={300}
          onRequestClose={() => setOpen(false)}
          className="bg-white border border-gray-200 rounded-md shadow-xl"
        >
          {availableOffices.length === 0 ? (
            <div className="text-gray-500 text-center text-xs py-4">No offices found</div>
          ) : (
            availableOffices.map((office) => {
              const isChecked = selectedCodes.includes(office.officeCode);
              return (
                <label
                  key={office.officeCode}
                  className="flex items-center gap-2 border-b border-gray-100 last:border-none px-3 py-2 hover:bg-[#f9fffa] cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => toggle(office.officeCode)}
                    className="accent-[#1B7F48] w-4 h-4 cursor-pointer"
                  />
                  <div className="flex flex-col leading-tight">
                    <span className="text-sm font-semibold text-gray-800 flex items-center gap-1">
                      <Building2 className="w-3.5 h-3.5 text-[#1B7F48]" />
                      {office.name || "Unnamed Office"}
                    </span>
                    <span className="text-[11px] text-gray-500">
                      {office.location
                        ? [office.location.city, office.location.state, office.location.country]
                            .filter(Boolean)
                            .join(", ")
                        : office.officeCode}
                    </span>
                  </div>
                </label>
              );
            })
          )}
        </AnchoredPanel>
      )}
    </div>
  );
};

export default OfficeMultiSelect;
